import { Component, OnChanges, OnInit, OnDestroy, Input, Output, EventEmitter, Directive } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../services/language.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Router } from '@angular/router';

import { FRListService } from '../services/factures-reb-list.service';
import { IFLInvoiceReb } from '../invoice-reb.interfaces';
import { resumeDataInterface } from '../../interfaces/resume';

@Directive()
class BaseFacturesRebListComponent implements OnChanges {
  @Input('elementList') elementList: any;
  @Input('resumeData') resumeData: resumeDataInterface;
  @Output('requestRows') requestRows = new EventEmitter();

  public activeFilters: any = {statePending: false, stateWaiting: true, stateCharged: false};
  public currentOrdering: any = {};
  public orderingCycle: Array<string> = ['', 'ASC', 'DESC'];
  public export: boolean = false;
  public firstRegisterShown: number;

  public lastRegisterShown: number;
  public rowsPerPageOptions: Array<number> = [5, 10, 20, 50, 100, 200, 500, 1000, 10000];
  public showFilters: boolean = true;
  public orderingParameters: any;

  private computeDisplayedRows(): void {
    this.firstRegisterShown = 1 + this.listService.currentPage * this.listService.rowsPerPage;
    this.lastRegisterShown = Math.min(this.firstRegisterShown + this.listService.rowsPerPage - 1, this.elementList.registers);
  }

  public exportar(){this.export = true;this.requestRowsToParent();};

  private requestRowsToParent(): void {
    this.requestRows.emit({
      skip : this.firstRegisterShown - 1,
      limit: this.listService.rowsPerPage,
      conds: { 'cnd_numero_factura': ' = ' },
      filters: this.activeFilters,
      ordering: this.currentOrdering,
      export: this.export
    });
    this.export = false;
  }

  public computeActiveFilters($event) {
    this.activeFilters = $event;
    if(this.activeFilters['statePending'])this.currentOrdering['data_factura_prevista'] = this.orderingCycle[1];
    if(this.activeFilters['stateWaiting'])this.currentOrdering['data_pagament_prevista'] = this.orderingCycle[1];
    if(this.activeFilters['stateCharged'])this.currentOrdering['data_pagament'] = this.orderingCycle[2];
    this.firstRegisterShown = 1 + this.listService.currentPage * this.listService.rowsPerPage;
    this.requestRowsToParent();
  }

  public cycleOrdering(key) {
    let i = this.orderingParameters[key]?this.orderingParameters[key]:0;

    this.orderingParameters = {};
    this.orderingParameters[key] = (1 + i) % this.orderingCycle.length;

    this.currentOrdering = {};
    this.currentOrdering[key] = this.orderingCycle[this.orderingParameters[key]];

    this.requestRowsToParent();
  }

  public requestPrevious(): void {
    this.listService.currentPage = Math.max(0, this.listService.currentPage - 1);
    this.computeDisplayedRows();
    this.requestRowsToParent();
  }

  public requestNext(): void {
    this.listService.currentPage = Math.min(Math.floor(this.elementList.registers / this.listService.rowsPerPage), this.listService.currentPage + 1);
    this.computeDisplayedRows();
    this.requestRowsToParent();
  }

  public gotoPage(page: number): void {
    this.listService.currentPage = page;
    this.computeDisplayedRows();
    this.requestRowsToParent();
  }

  public navigateToProject(project) {
    this.router.navigate([this.routeKey, project.codi]);
  }

  public setRowPerPage(rowsPerPage: number) {
    this.listService.rowsPerPage = rowsPerPage;
    this.requestRowsToParent();
  }

  public toggleFiltersVisibility() {
    this.showFilters = !this.showFilters;
  }

  constructor(public translate: TranslateService, public languageService: LanguageService, 
              public listService: FRListService, private router: Router, public tableFields, private routeKey: string,
              public facturesFilters: Array<any>,public ordreCamps: Array<any>) {
    this.orderingParameters = this.tableFields.reduce((acum, curr) => {
      acum[curr[0]] = 0;
      return acum;
    }, {});
    for(var i=0;i<ordreCamps.length;i++){this.orderingParameters[ordreCamps[i].key] = ordreCamps[i].sort;this.cycleOrdering(ordreCamps[i].key);}
  }

  public goProject(codi){ this.router.navigate(['project',codi]);};

  ngOnChanges(changes: any): void {
    const newElementList = changes.elementList.currentValue;
    if (newElementList) {
      this.computeDisplayedRows();
    }
  }
}

@Component({
  selector: 'mcrit-gestio-factures-reb-list',
  templateUrl: 'factures-reb-list.component.html',
  styleUrls: ['factures-reb-list.component.css'],
  providers: []
})
export class FacturesRebListComponent extends BaseFacturesRebListComponent implements OnInit, OnDestroy {
  @Input('elementList') elementList: any;
  @Input('resumeData') resumeData: resumeDataInterface;
  @Output('requestRows') requestRows = new EventEmitter();

  public title: string;
  public invoicingData: Array<IFLInvoiceReb>;

  private unsubscriber = new Subject<void>();

  public isDate(field: string): boolean {
    return (field === 'data_factura' || field === 'data_pagament');
  }

  // Mètode ESTÀTIC perquè es pot cridar dins de super(...) abans que 'this' existeixi,
  // i es torna a cridar des de ngOnInit quan canvia l'idioma (via this.translate).
  private static buildTableFields(translate: TranslateService): Array<[string, string]> {
    return [
      ['numero_factura', translate.instant('facturesRebList.colNumFactura')],
      ['projecte_codi', translate.instant('facturesRebList.colCodi')],
      ['data_factura', translate.instant('facturesRebList.colDataFacturacio')],
      ['data_pagament', translate.instant('facturesRebList.colDataPagament')],
      ['facturacio_prevista_eur', translate.instant('facturesRebList.colFacturacioPrevistaEur')],
      ['facturacio_loc', translate.instant('facturesRebList.colFacturacioPrevistaLocal')],
      ['facturacio_prevista_percent', translate.instant('facturesRebList.colFacturacioPrevistaPercent')],
      ['fita', translate.instant('facturesRebList.colFita')],
      ['objecte_lliurament', translate.instant('facturesRebList.colObjecte')],
      ['concepte', translate.instant('facturesRebList.colConcepte')],
      ['activa', translate.instant('facturesRebList.colActiva')],
      ['observacions', translate.instant('facturesRebList.colObservacions')]
    ];
  }

  private static buildFilters(translate: TranslateService, listService: FRListService): Array<any> {
    return [
      {
        inputs: [
          {
            name: 'invoiceState',
            type: 'checkbox',
            values: [
              {
                text: translate.instant('facturesRebList.chkPendentsRebre'),
                name: 'statePending',
                value: false
              },
              {
                text: translate.instant('facturesRebList.chkPendentsPagar'),
                name: 'stateWaiting',
                value: true
              },
              {
                text: translate.instant('facturesRebList.chkPagades'),
                name: 'stateCharged',
                value: false
              }
            ]
          }
        ]
      },
      {
        inputs: [
          {
            name: 'text',
            type: 'text',
            values: [
              {
                text: translate.instant('facturesRebList.colCodi'), //Text a mostrar label
                name: 'projecte_codi', //Camp de la base de dades
                autocomplete: 'codiClau', //Crida per obtenir els valors si autocomplete
                value: listService.getSearchValues('projecte_codi') //valor a mostrar
              },
              {
                text: translate.instant('facturesRebList.colNumFactura'),
                name: 'numero_factura',
                autocomplete: 'numFacturaReb',
                value: listService.getSearchValues('numero_factura')
              },
              {
                text: translate.instant('facturesRebList.colConcepte'),
                name: 'concepte',
                autocomplete: 'concepteFacturaReb',
                value: listService.getSearchValues('concepte')
              },
              {
                text: translate.instant('facturesRebList.filtreDataFactura'),
                name: 'data_factura',
                value: ''
              },
              {
                text: translate.instant('facturesRebList.filtreDataFacturaPrevista'),
                name: 'data_factura_prevista',
                value: ''
              },
              {
                text: translate.instant('facturesRebList.filtreDataPagament'),
                name: 'data_pagament',
                value: ''
              },
              {
                text: translate.instant('facturesRebList.filtreDataPagamentPrevista'),
                name: 'data_pagament_prevista',
                value: ''
              }
            ]
          }
        ]
      }
    ];
  }

  constructor(public translate: TranslateService, public languageService: LanguageService, listService: FRListService, router: Router) {
    super(
      translate,
      languageService,
      listService,
      router,
      FacturesRebListComponent.buildTableFields(translate),
      'project',
      FacturesRebListComponent.buildFilters(translate, listService),
      [{key: 'data_factura_prevista', sort: 1}]
    );
    this.title = translate.instant('facturesRebList.title');
  }

  ngOnInit() {
    this.computeActiveFilters({stateWaiting: true});

    // Si l'usuari canvia d'idioma en calent, reconstruïm títol, capçaleres i filtres
    this.translate.onLangChange
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(() => {
        this.title = this.translate.instant('facturesRebList.title');
        this.tableFields = FacturesRebListComponent.buildTableFields(this.translate);
        this.facturesFilters = FacturesRebListComponent.buildFilters(this.translate, this.listService);
      });
  }

  ngOnDestroy() {
    this.unsubscriber.next();
    this.unsubscriber.complete();
  }
}
